import { FC, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { cssX, ID } from '@datassential/platform-ui-lib';
import { AiOutlineCheck } from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';
import { AppContext } from '../../../../AppContext';
import { AvailableWorkspace, CHAIN_ENABLED_WORKSPACES, isWorkspaceAccessible, WorkspaceEnum } from '@models/Workspace';
import { routeNames } from '@core/routeNames';
import { useHomeWorkspaceResolution } from '@pages/Home/context/WorkspaceResolutionContext';
import Skeleton from '@components/Skeleton/Skeleton';
import LineSpinner from '@components/new/Select/LineSpinner/LineSpinner';
import './WorkspaceSwitcher.scss';

const getWorkspaceLabel = (workspace: AvailableWorkspace, all: AvailableWorkspace[]): string => {
    const duplicates = all.filter(w => w.displayName === workspace.displayName);
    return duplicates.length > 1 ? workspace.name : workspace.displayName;
};

const WorkspaceSwitcher: FC = () => {
    const history = useHistory();
    const { globalVariables } = useContext(AppContext);
    const { availableWorkspaces, availableChains } = globalVariables;

    const resolution = useHomeWorkspaceResolution();

    const [isWorkspacesOpen, setIsWorkspacesOpen] = useState(false);
    const [isChainsOpen, setIsChainsOpen] = useState(false);
    const [pendingWorkspaceId, setPendingWorkspaceId] = useState<WorkspaceEnum | null>(null);

    const {
        setWorkspaceId,
        setChainId,
        workspaceId: activeWorkspaceId,
        chainId: activeChainId,
        isSavingWorkspace,
    } = resolution;

    const activeWorkspace = useMemo(() => (
        activeWorkspaceId
            ? availableWorkspaces.find(w => w.id === activeWorkspaceId) ?? null
            : null
    ), [activeWorkspaceId, availableWorkspaces]);

    const requiresChain = useMemo(() => (
        activeWorkspaceId !== null && CHAIN_ENABLED_WORKSPACES.includes(activeWorkspaceId)
    ), [activeWorkspaceId]);

    const handleSelectWorkspace = useCallback(async (
        workspace: AvailableWorkspace,
        event: MouseEvent<HTMLButtonElement>,
    ) => {
        if (!isWorkspaceAccessible(workspace)) return;

        const needsChain = CHAIN_ENABLED_WORKSPACES.includes(workspace.id) && availableChains.length > 0;
        if (needsChain) {
            event.stopPropagation();
        } else if (requiresChain) {
            setChainId(null);
        }

        setPendingWorkspaceId(workspace.id);
        const succeeded = await setWorkspaceId(workspace.id);
        setPendingWorkspaceId(null);

        if (succeeded) {
            setIsWorkspacesOpen(false);
            history.replace(routeNames.home);
        }
    }, [setWorkspaceId, setChainId, requiresChain, history, availableChains.length]);

    const handleSelectChain = useCallback((chainId: ID) => {
        setChainId(chainId);
        setIsChainsOpen(false);
        history.replace(routeNames.home);
    }, [setChainId, history]);

    if (!resolution.isReady) return null;
    if (!activeWorkspace) {
        console.error(`Could not find active workspace for ${activeWorkspaceId}`);
        return null;
    }

    const activeLabel = getWorkspaceLabel(activeWorkspace, availableWorkspaces);
    const activeChain = availableChains.find(c => c.id === activeChainId);

    return (
        <div className="WorkspaceSwitcher">
            <button
                type="button"
                className={cn(cssX('Dropdown__item'), 'is-tall', 'WorkspaceSwitcher__row')}
                aria-expanded={isWorkspacesOpen}
                aria-controls="WorkspaceSwitcher__workspaces"
                aria-label={isSavingWorkspace ? 'Saving workspace selection…' : undefined}
                aria-busy={isSavingWorkspace}
                disabled={isSavingWorkspace}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsWorkspacesOpen(open => !open);
                }}
                data-testid="buttonWorkspaceSwitcher"
            >
                {isSavingWorkspace ? (
                    <span className="WorkspaceSwitcher__row-label" aria-live="polite">
                        <Skeleton width={140} height={16} testId="skeletonWorkspaceSwitcher"/>
                    </span>
                ) : (
                    <span className="WorkspaceSwitcher__row-label">
                        Personalized Workspace: {activeLabel}
                    </span>
                )}
                <IoIosArrowDown
                    className={cn('WorkspaceSwitcher__chevron', isWorkspacesOpen && 'is-open')}
                />
            </button>

            <div
                id="WorkspaceSwitcher__workspaces"
                className={cn('WorkspaceSwitcher__submenu', isWorkspacesOpen && 'is-open')}
                aria-hidden={!isWorkspacesOpen}
            >
                <div className="WorkspaceSwitcher__submenu-inner">
                    {availableWorkspaces.map(workspace => {
                        const accessible = isWorkspaceAccessible(workspace);
                        const isActive = workspace.id === activeWorkspace.id;
                        return (
                            <button
                                key={workspace.id}
                                type="button"
                                className={cn(
                                    'WorkspaceSwitcher__option',
                                    !accessible && 'is-disabled',
                                    isActive && 'is-active',
                                )}
                                disabled={!accessible || isSavingWorkspace}
                                tabIndex={isWorkspacesOpen ? 0 : -1}
                                onClick={(e) => handleSelectWorkspace(workspace, e)}
                                data-testid={`buttonWorkspaceOption_${workspace.id}`}
                            >
                                <span className="WorkspaceSwitcher__option-check">
                                    {pendingWorkspaceId === workspace.id
                                        ? <LineSpinner/>
                                        : isActive && <AiOutlineCheck/>}
                                </span>
                                <span className="WorkspaceSwitcher__option-label">
                                    {getWorkspaceLabel(workspace, availableWorkspaces)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {requiresChain && availableChains.length > 0 && (
                <>
                    <button
                        type="button"
                        className={cn(cssX('Dropdown__item'), 'is-tall', 'WorkspaceSwitcher__row')}
                        aria-expanded={isChainsOpen}
                        aria-controls="WorkspaceSwitcher__chains"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsChainsOpen(open => !open);
                        }}
                        data-testid="buttonChainSwitcher"
                    >
                        <span className="WorkspaceSwitcher__row-label">
                            Chain: {activeChain?.name ?? '-'}
                        </span>
                        <IoIosArrowDown
                            className={cn('WorkspaceSwitcher__chevron', isChainsOpen && 'is-open')}
                        />
                    </button>

                    <div
                        id="WorkspaceSwitcher__chains"
                        className={cn('WorkspaceSwitcher__submenu', isChainsOpen && 'is-open')}
                        aria-hidden={!isChainsOpen}
                    >
                        <div className="WorkspaceSwitcher__submenu-inner">
                            {availableChains.map(chain => {
                                const isActive = chain.id === activeChainId;
                                return (
                                    <button
                                        key={chain.id}
                                        type="button"
                                        className={cn(
                                            'WorkspaceSwitcher__option',
                                            isActive && 'is-active',
                                        )}
                                        tabIndex={isChainsOpen ? 0 : -1}
                                        onClick={() => handleSelectChain(chain.id)}
                                        data-testid={`buttonChainOption_${chain.id}`}
                                    >
                                        <span className="WorkspaceSwitcher__option-check">
                                            {isActive && <AiOutlineCheck/>}
                                        </span>
                                        <span className="WorkspaceSwitcher__option-label">
                                            {chain.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            <div className="WorkspaceSwitcher__separator"/>
        </div>
    );
};

export default WorkspaceSwitcher;
